import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationsContext';
import { notificationsService } from '../services/notificationsService';
import { formatRelativeTime } from '../utils/formatRelativeTime';
import GateHausLogo from './GateHausLogo';
import '../styles/dashboard.css';

interface DashboardLayoutProps {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
}

const NAV_SECTIONS = [
  {
    title: 'Overview',
    links: [
      { label: 'Dashboard', to: '/dashboard' },
      { label: 'Notifications', to: '/notifications' }
    ]
  },
  {
    title: 'Identity',
    links: [{ label: 'Profile', to: '/identity' }]
  },
  {
    title: 'Security',
    links: [{ label: 'Security Centre', to: '/security' }]
  },
  {
    title: 'Apps',
    links: [{ label: 'Apps & Connections', to: '/apps' }]
  },
  {
    title: 'Support',
    links: [{ label: 'Contact Support', to: '/support' }]
  }
];

const DashboardLayout = ({ title, actions, children }: DashboardLayoutProps) => {
  const { user, logout } = useAuth();
  const { notifications, loading: notificationsLoading, unreadCount, error: notificationsError } = useNotifications();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationsRef = useRef<HTMLDivElement | null>(null);

  const previewNotifications = useMemo(() => notifications.slice(0, 5), [notifications]);

  const toggleNotifications = () => {
    setNotificationsOpen((open) => !open);
  };

  useEffect(() => {
    if (!notificationsOpen) return;

    const handleClick = (event: MouseEvent) => {
      if (!notificationsRef.current?.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [notificationsOpen]);

  return (
    <div className="dashboard-layout">
      <div className="brand-bar">
        <GateHausLogo />
        <div className="brand-actions">
          <div className="notifications-wrapper" ref={notificationsRef}>
            <button
              className="notifications-bell"
              type="button"
              aria-label="Notifications"
              aria-haspopup="dialog"
              aria-expanded={notificationsOpen}
              onClick={toggleNotifications}
            >
              <i className="ri-notification-3-line" aria-hidden="true" />
              {unreadCount > 0 && <span className="notifications-count">{unreadCount}</span>}
            </button>
            {notificationsOpen && (
              <>
                <div className="notifications-scrim" onClick={() => setNotificationsOpen(false)} aria-hidden="true" />
                <div className="notifications-popover" role="dialog" aria-label="Notifications">
                  <div className="notifications-popover-header">
                    <strong>Notifications</strong>
                    <NavLink to="/notifications" className="notifications-link" onClick={() => setNotificationsOpen(false)}>
                      View all
                    </NavLink>
                  </div>
                  <div className="notifications-popover-body">
                    {notificationsLoading ? (
                      <p className="notifications-muted">Loading notifications…</p>
                    ) : notificationsError ? (
                      <p className="notifications-muted">{notificationsError}</p>
                    ) : previewNotifications.length === 0 ? (
                      <p className="notifications-muted">No notifications yet.</p>
                    ) : (
                      previewNotifications.map((item) => (
                        <div key={item.id} className={`notification-preview ${item.viewed_at ? '' : 'unread'}`}>
                          <div className="notification-preview-header">
                            <div>
                              <strong>{item.title}</strong>
                              <span title={new Date(item.sent_at).toLocaleString()}>
                                {formatRelativeTime(item.sent_at)}
                              </span>
                            </div>
                            {!item.viewed_at && <span className="notification-dot" aria-label="Unread" />}
                          </div>
                          <p>{item.body}</p>
                          {item.ctas && item.ctas.length > 0 && (
                            <div className="notification-actions">
                              {item.ctas.map((cta, index) => (
                                <a
                                  key={`${item.id}-${index}`}
                                  href={notificationsService.safeLinkUrl(cta.url)}
                                  className="notification-link"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {cta.label}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          <button className="menu-toggle" aria-label="Toggle navigation" onClick={() => setSidebarOpen((open) => !open)}>
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>
      <div className={`dashboard-shell ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="mobile-logo">
            <GateHausLogo />
          </div>
          <button className="sidebar-close" aria-label="Close navigation" onClick={() => setSidebarOpen(false)}>
            <span />
            <span />
            <span />
          </button>
          <div className="user-panel">
            <div className="user-block">
              <img
                src={user?.profile_picture || 'https://placehold.co/80x80?text=GH'}
                alt="Profile"
                width={64}
                height={64}
                style={{ borderRadius: '50%', objectFit: 'cover' }}
              />
              <div className="user-meta">
                <strong>
                  {user?.first_name} {user?.surname}
                </strong>
                <span>{user?.email}</span>
              </div>
            </div>
          </div>
          <nav className="sidebar-nav">
            {NAV_SECTIONS.map((section) => (
              <div key={section.title}>
                <p className="sidebar-heading">{section.title}</p>
                <ul>
                  {section.links.map((link) => (
                    <li key={link.to}>
          <NavLink
            to={link.to}
            className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
            onClick={() => setSidebarOpen(false)}
          >
            {link.label}
          </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
          <button onClick={logout}>Sign out</button>
        </aside>
        <main className="dashboard-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: 0 }}>{title || 'Identity dashboard'}</h2>
              <p style={{ color: 'var(--gray)', marginTop: '0.4rem' }}>Manage your Gate.haus identity, security, and apps.</p>
            </div>
            {actions}
          </div>
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
